// src/pages/expert/ArtistQuotes.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabaseClient";

const METHOD_GROUPS = {
  direct_cup: "directCup",
  direct_cup_prudent: "directCupPrudent",
  analogy_cup: "analogyCup",
  analogy_cup_ratio: "analogyCupRatio",
  hybrid_points_cup: "hybridPointsCup",
  point_method: "pointMethod",
};

const CONFIDENCE = {
  low: "low",
  med: "medium",
  high: "high",
};

const DIRECT_CUP_METHODS = new Set(["direct_cup", "direct_cup_prudent"]);
const ANALOGY_METHODS = new Set(["analogy_cup", "analogy_cup_ratio"]);
const POINT_METHODS = new Set(["point_method", "hybrid_points_cup"]);

function isDirectCupLike(methodGroup) {
  return DIRECT_CUP_METHODS.has(methodGroup);
}

function isAnalogyLike(methodGroup) {
  return ANALOGY_METHODS.has(methodGroup);
}

function isPointLike(methodGroup) {
  return POINT_METHODS.has(methodGroup);
}

function normalizeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function formatEuro(value) {
  const n = toNumber(value);
  if (n === null) return "—";
  return `${Math.round(n).toLocaleString("fr-FR")} €`;
}

function clampPct(value, fallback = 100) {
  const n = toNumber(value);
  if (n === null) return fallback;
  return Math.max(0, Math.min(100, n));
}

function emptyAnalogComparable() {
  return {
    artist: "",
    reason: "",
    source: "",
    observed_low: "",
    observed_high: "",
    discount_pct: "",
    weight_pct: "",
    market_role: "trend_only",
    market_tier: "equivalent",
    career_discount_pct: "",
    price_weight_max_pct: "",
    expert_override: false,
    dimension_fit_pct: "100",
    technique_fit_pct: "100",
    proximity_fit_pct: "100",
    freshness_fit_pct: "100",
  };
}

function emptyDirectComparableSettings() {
  return {
    own_recent_weight_pct: "60",
    own_3to5_weight_pct: "30",
    gallery_cap_value: "",
    dimension_fit_pct: "100",
    technique_fit_pct: "100",
    proximity_fit_pct: "100",
    freshness_fit_pct: "100",
    min_references: "3",
  };
}

const emptyForm = {
  artist: "",
  medium: "",
  method_group: "direct_cup",
  price_low: "",
  price_high: "",
  average_value: "",
  point_rate_low_eur: "",
  point_rate_high_eur: "",
  analog_artists_text: "",
  analogy_reason: "",
  direct_comparable_settings: emptyDirectComparableSettings(),
  analog_comparables: [emptyAnalogComparable(), emptyAnalogComparable(), emptyAnalogComparable()],
  confidence: "med",
  quote_date: todayISO(),
  notes: "",
};

function computeGroupBAnalogRange(form) {
  const rows = (form.analog_comparables || []).filter((row) => {
    return row.artist && (toNumber(row.observed_low) || toNumber(row.observed_high));
  });

  if (!rows.length) return null;

  const fallbackWeight = 100 / rows.length;
  const computed = rows.map((row) => {
    const low = toNumber(row.observed_low) || toNumber(row.observed_high) || 0;
    const high = toNumber(row.observed_high) || toNumber(row.observed_low) || 0;
    const discount = clampPct(row.discount_pct, 0);
    const careerDiscount = clampPct(row.career_discount_pct, 0);
    const maxWeight = toNumber(row.price_weight_max_pct);
    const rawWeight = toNumber(row.weight_pct) && toNumber(row.weight_pct) > 0 ? toNumber(row.weight_pct) : fallbackWeight;
    const weight = maxWeight && maxWeight > 0 ? Math.min(rawWeight, maxWeight) : rawWeight;
    const dimensionFit = clampPct(row.dimension_fit_pct, 100) / 100;
    const techniqueFit = clampPct(row.technique_fit_pct, 100) / 100;
    const proximityFit = clampPct(row.proximity_fit_pct, 100) / 100;
    const freshnessFit = clampPct(row.freshness_fit_pct, 100) / 100;
    const fitFactor = dimensionFit * techniqueFit * proximityFit * freshnessFit;
    const discountFactor = Math.max(0, 1 - discount / 100);
    const careerDiscountFactor = Math.max(0, 1 - careerDiscount / 100);

    return {
      ...row,
      observed_low: low,
      observed_high: high,
      discount_pct: discount,
      career_discount_pct: careerDiscount,
      price_weight_max_pct: maxWeight || "",
      weight_pct: weight,
      dimension_fit_pct: Math.round(dimensionFit * 100),
      technique_fit_pct: Math.round(techniqueFit * 100),
      proximity_fit_pct: Math.round(proximityFit * 100),
      freshness_fit_pct: Math.round(freshnessFit * 100),
      decision_factor_pct: Math.round(fitFactor * 100),
      adjusted_low: Math.round(low * fitFactor * discountFactor * careerDiscountFactor),
      adjusted_high: Math.round(high * fitFactor * discountFactor * careerDiscountFactor),
    };
  });

  const totalWeight = computed.reduce((sum, row) => sum + row.weight_pct, 0) || 100;
  const low = computed.reduce((sum, row) => sum + row.adjusted_low * (row.weight_pct / totalWeight), 0);
  const high = computed.reduce((sum, row) => sum + row.adjusted_high * (row.weight_pct / totalWeight), 0);

  return {
    low: Math.round(low),
    high: Math.round(high),
    average: Math.round((low + high) / 2),
    rows: computed,
  };
}

function buildGroupANotes(form) {
  if (!isDirectCupLike(form.method_group)) return null;

  const settings = { ...emptyDirectComparableSettings(), ...(form.direct_comparable_settings || {}) };
  const lines = [];
  if (form.notes) lines.push(form.notes);
  lines.push(
    `Groupe A — CUP directe : comparaison améliorée entre ventes du même artiste, avec pondération dimensions / technique / proximité de format / fraîcheur.`
  );
  lines.push(
    `Pondération fraîcheur : résultats propres < 3 ans ${settings.own_recent_weight_pct || 60}%, résultats propres 3–5 ans ${settings.own_3to5_weight_pct || 30}%, résultats > 5 ans exclus du calcul sauf justification.`
  );
  lines.push(
    `Coefficients décisionnels appliqués : dimensions ${settings.dimension_fit_pct || 100}%, technique ${settings.technique_fit_pct || 100}%, proximité ${settings.proximity_fit_pct || 100}%, fraîcheur ${settings.freshness_fit_pct || 100}%.`
  );
  if (settings.gallery_cap_value) {
    lines.push(`Prix galerie / primaire utilisé uniquement comme borne haute : ${formatEuro(settings.gallery_cap_value)}.`);
  }

  return lines.filter(Boolean).join("\n") || null;
}

function buildGroupBNotes(form, analogRange) {
  if (!isAnalogyLike(form.method_group)) return form.notes || null;

  const lines = [];
  if (form.notes) lines.push(form.notes);

  if (analogRange?.rows?.length) {
    lines.push(
      `Groupe B — CUP par analogie : ${analogRange.rows.length} artiste(s) comparable(s), avec décote expert et coefficients décisionnels dimensions / technique / proximité / fraîcheur.`
    );

    analogRange.rows.forEach((row) => {
      lines.push(
        `${row.artist} : ${formatEuro(row.observed_low)}–${formatEuro(row.observed_high)}, décote ${row.discount_pct || 0}%, poids ${Math.round(row.weight_pct)}%, décision ${row.decision_factor_pct}% (dim. ${row.dimension_fit_pct}%, tech. ${row.technique_fit_pct}%, proximité ${row.proximity_fit_pct}%, fraîcheur ${row.freshness_fit_pct}%)${row.reason ? ` — ${row.reason}` : ""}${row.source ? ` — source : ${row.source}` : ""}.`
      );
    });

    lines.push(`Fourchette analogique après décote et coefficients : ${formatEuro(analogRange.low)}–${formatEuro(analogRange.high)}.`);
  }

  return lines.join("\n") || null;
}

export default function ArtistQuotes() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const tr = (key, options) => t(`artistQuotes.${key}`, options);
  const formatMoney = (value) => {
    const n = toNumber(value);
    if (n === null) return "—";
    return new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language || "fr", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(Math.round(n));
  };

  const [currentUserId, setCurrentUserId] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    setLoading(true);
    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData?.session;

    if (!session) {
      navigate("/login");
      return;
    }

    setCurrentUserId(session.user.id);

    const { data, error } = await supabase
      .from("artist_quotes")
      .select("*")
      .eq("expert_id", session.user.id)
      .order("updated_at", { ascending: false });

    if (error) setStatus({ type: "error", message: error.message });
    else setQuotes(data || []);

    setLoading(false);
  }

  const filteredQuotes = useMemo(() => {
    const q = normalizeKey(search);
    if (!q) return quotes;
    return quotes.filter((quote) => normalizeKey(`${quote.artist} ${quote.medium} ${quote.notes || ""}`).includes(q));
  }, [quotes, search]);

  const groupBAnalogRange = useMemo(() => computeGroupBAnalogRange(form), [form]);

  function resetForm() {
    setEditingId(null);
    setForm({
      ...emptyForm,
      quote_date: todayISO(),
      direct_comparable_settings: emptyDirectComparableSettings(),
      analog_comparables: [emptyAnalogComparable(), emptyAnalogComparable(), emptyAnalogComparable()],
    });
  }

  function applyMicroSegmentPreset() {
    setEditingId(null);
    setForm({
      ...emptyForm,
      artist: "Yuki ONODERA",
      medium: "Transvest series — grands formats gélatino-argentiques",
      method_group: "direct_cup_prudent",
      price_low: "3000",
      price_high: "5500",
      average_value: "4200",
      confidence: "med",
      quote_date: todayISO(),
      direct_comparable_settings: {
        ...emptyDirectComparableSettings(),
        own_recent_weight_pct: "60",
        own_3to5_weight_pct: "30",
        gallery_cap_value: "",
        dimension_fit_pct: "95",
        technique_fit_pct: "95",
        proximity_fit_pct: "100",
        freshness_fit_pct: "80",
        min_references: "5",
        micro_segment: "Transvest",
        segment_logic: "Même série, formats proches, techniques photo comparables, résultats Artprice 2006–2025.",
      },
      analog_comparables: [emptyAnalogComparable(), emptyAnalogComparable(), emptyAnalogComparable()],
      notes:
        "Série Transvest. Marché secondaire faible mais cohérent ; comparables directs identifiés entre 2006 et 2025 sur formats homogènes. Ligne volontairement séparée de la photographie générale Yuki Onodera afin de préserver la cohérence série / format / technique.",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function editQuote(quote) {
    const analogComparables = Array.isArray(quote.analog_comparables)
      ? quote.analog_comparables.map((item) => ({ ...emptyAnalogComparable(), ...item }))
      : Array.isArray(quote.analog_artists)
        ? quote.analog_artists.map((artist) => ({ ...emptyAnalogComparable(), artist }))
        : [];

    while (analogComparables.length < 3) analogComparables.push(emptyAnalogComparable());

    setEditingId(quote.id);
    setForm({
      artist: quote.artist || "",
      medium: quote.medium || "",
      method_group: quote.method_group || "direct_cup",
      price_low: quote.price_low ?? "",
      price_high: quote.price_high ?? "",
      average_value: quote.average_value ?? "",
      point_rate_low_eur: quote.point_rate_low_eur ?? "",
      point_rate_high_eur: quote.point_rate_high_eur ?? "",
      analog_artists_text: Array.isArray(quote.analog_artists) ? quote.analog_artists.join(", ") : "",
      analogy_reason: quote.analogy_reason || "",
      direct_comparable_settings: {
        ...emptyDirectComparableSettings(),
        ...(quote.direct_comparable_settings || {}),
      },
      analog_comparables: analogComparables,
      confidence: quote.confidence || "med",
      quote_date: quote.quote_date || todayISO(),
      notes: quote.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function updateAnalogComparable(index, patch) {
    setForm((prev) => {
      const next = [...(prev.analog_comparables || [])];
      next[index] = { ...(next[index] || emptyAnalogComparable()), ...patch };
      return { ...prev, analog_comparables: next };
    });
  }

  function addAnalogComparable() {
    setForm((prev) => ({ ...prev, analog_comparables: [...(prev.analog_comparables || []), emptyAnalogComparable()] }));
  }

  function removeAnalogComparable(index) {
    setForm((prev) => {
      const next = (prev.analog_comparables || []).filter((_, i) => i !== index);
      return { ...prev, analog_comparables: next.length ? next : [emptyAnalogComparable()] };
    });
  }

  async function saveQuote() {
    if (!currentUserId) return;
    if (!form.artist.trim() || !form.medium.trim()) {
      setStatus({ type: "error", message: tr("messages.required") });
      return;
    }

    const analogRange = computeGroupBAnalogRange(form);
    const low = isAnalogyLike(form.method_group) && analogRange ? analogRange.low : toNumber(form.price_low);
    const high = isAnalogyLike(form.method_group) && analogRange ? analogRange.high : toNumber(form.price_high);
    const average =
      isAnalogyLike(form.method_group) && analogRange
        ? analogRange.average
        : toNumber(form.average_value) || (low && high ? Math.round((low + high) / 2) : 0);

    const directSettings = {
      ...emptyDirectComparableSettings(),
      ...(form.direct_comparable_settings || {}),
    };

    const directComparableSettings = {
      own_recent_weight_pct: clampPct(directSettings.own_recent_weight_pct, 60),
      own_3to5_weight_pct: clampPct(directSettings.own_3to5_weight_pct, 30),
      gallery_cap_value: toNumber(directSettings.gallery_cap_value),
      dimension_fit_pct: clampPct(directSettings.dimension_fit_pct, 100),
      technique_fit_pct: clampPct(directSettings.technique_fit_pct, 100),
      proximity_fit_pct: clampPct(directSettings.proximity_fit_pct, 100),
      freshness_fit_pct: clampPct(directSettings.freshness_fit_pct, 100),
      min_references: toNumber(directSettings.min_references) || 3,
    };

    const analogComparables = (form.analog_comparables || [])
      .filter((row) => row.artist || row.reason || row.source || row.observed_low || row.observed_high)
      .map((row) => ({
        artist: String(row.artist || "").trim(),
        reason: row.reason || "",
        source: row.source || "",
        observed_low: toNumber(row.observed_low),
        observed_high: toNumber(row.observed_high),
        discount_pct: clampPct(row.discount_pct, 0),
        weight_pct: toNumber(row.weight_pct),
        market_role: row.market_role || "trend_only",
        market_tier: row.market_tier || "equivalent",
        career_discount_pct: clampPct(row.career_discount_pct, 0),
        price_weight_max_pct: toNumber(row.price_weight_max_pct),
        expert_override: Boolean(row.expert_override),
        dimension_fit_pct: clampPct(row.dimension_fit_pct, 100),
        technique_fit_pct: clampPct(row.technique_fit_pct, 100),
        proximity_fit_pct: clampPct(row.proximity_fit_pct, 100),
        freshness_fit_pct: clampPct(row.freshness_fit_pct, 100),
      }));

    const analogMarketRules = Object.fromEntries(
      analogComparables
        .filter((row) => row.artist)
        .map((row) => [
          row.artist,
          {
            role: row.market_role || "trend_only",
            market_tier: row.market_tier || "equivalent",
            career_discount: (toNumber(row.career_discount_pct) || 0) / 100,
            price_weight_max: row.price_weight_max_pct ? (toNumber(row.price_weight_max_pct) || 0) / 100 : null,
            expert_override: Boolean(row.expert_override),
            reason: row.reason || null,
          },
        ])
    );

    setSaving(true);
    setStatus(null);

    const payload = {
      expert_id: currentUserId,
      artist: form.artist.trim(),
      medium: form.medium.trim(),
      artist_key: normalizeKey(form.artist),
      medium_key: normalizeKey(form.medium),
      method_group: form.method_group,
      price_low: low,
      price_high: high,
      average_value: average,
      point_rate_low_eur: toNumber(form.point_rate_low_eur),
      point_rate_high_eur: toNumber(form.point_rate_high_eur),
      recommended_point_rate_eur:
        toNumber(form.point_rate_low_eur) && toNumber(form.point_rate_high_eur)
          ? (toNumber(form.point_rate_low_eur) + toNumber(form.point_rate_high_eur)) / 2
          : null,
      analog_artists:
        isAnalogyLike(form.method_group)
          ? analogComparables.map((row) => row.artist).filter(Boolean)
          : form.analog_artists_text
            ? form.analog_artists_text.split(",").map((v) => v.trim()).filter(Boolean)
            : null,
      analog_comparables: isAnalogyLike(form.method_group) ? analogComparables : [],
      analog_market_rules: isAnalogyLike(form.method_group) ? analogMarketRules : {},
      direct_comparable_settings: isDirectCupLike(form.method_group) ? directComparableSettings : {},
      analogy_reason: form.analogy_reason || null,
      confidence: form.confidence,
      quote_date: form.quote_date || todayISO(),
      notes:
        isDirectCupLike(form.method_group)
          ? buildGroupANotes({ ...form, direct_comparable_settings: directComparableSettings })
          : buildGroupBNotes({ ...form, notes: form.notes }, analogRange),
      updated_at: new Date().toISOString(),
    };

    const query = editingId
      ? supabase.from("artist_quotes").update(payload).eq("id", editingId)
      : supabase.from("artist_quotes").upsert(payload, { onConflict: "expert_id,artist_key,medium_key" });

    const { error } = await query;

    if (error) {
      setStatus({ type: "error", message: error.message });
    } else {
      setStatus({ type: "success", message: tr("messages.saved") });
      resetForm();
      await load();
    }

    setSaving(false);
  }

  async function deleteQuote(id) {
    const ok = window.confirm(tr("messages.confirmDelete"));
    if (!ok) return;
    const { error } = await supabase.from("artist_quotes").delete().eq("id", id);
    if (error) setStatus({ type: "error", message: error.message });
    else {
      setStatus({ type: "success", message: tr("messages.deleted") });
      await load();
    }
  }

  if (loading) {
    return <div className="max-w-6xl mx-auto px-6 py-12 text-sm text-gray-500">{tr("loading")}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <button type="button" onClick={() => navigate("/expert/dashboard?tab=outils")} className="text-sm text-gray-500 hover:text-black mb-4">
            ← {tr("backToTools")}
          </button>
          <p className="text-xs uppercase tracking-widest text-purple-500 mb-2">{tr("kicker")}</p>
          <h1 className="text-4xl font-serif mb-2">{tr("title")}</h1>
          <p className="text-sm text-gray-500 max-w-3xl">
            {tr("description")}
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {status && (
          <div className={`mb-6 border-l-4 p-4 text-sm ${status.type === "success" ? "bg-green-50 border-green-500 text-green-800" : "bg-red-50 border-red-500 text-red-800"}`}>
            {status.message}
          </div>
        )}

        <section className="bg-amber-50 border border-amber-200 p-5 mb-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-amber-700 mb-2">{tr("microSegment.kicker")}</p>
              <h2 className="text-xl font-serif text-amber-950 mb-2">{tr("microSegment.title")}</h2>
              <p className="text-sm text-amber-900 max-w-4xl">
                {tr("microSegment.description")}
              </p>
              <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-amber-900">
                <li>• {tr("microSegment.item1")}</li>
                <li>• {tr("microSegment.item2")}</li>
                <li>• {tr("microSegment.item3")}</li>
                <li>• {tr("microSegment.item4")}</li>
              </ul>
            </div>
            <button type="button" onClick={applyMicroSegmentPreset} className="shrink-0 bg-amber-900 text-white px-4 py-2 text-xs hover:bg-black">
              {tr("microSegment.prefill")}
            </button>
          </div>
        </section>

        <section className="bg-white border border-gray-200 p-6 mb-8">
          <h2 className="text-2xl font-serif mb-5">{editingId ? tr("form.editTitle") : tr("form.newTitle")}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.artist")} />
            <input value={form.medium} onChange={(e) => setForm({ ...form, medium: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.medium")} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <select value={form.method_group} onChange={(e) => setForm({ ...form, method_group: e.target.value })} className="border border-gray-200 px-3 py-2">
              {Object.entries(METHOD_GROUPS).map(([key, label]) => <option key={key} value={key}>{tr(`methods.${label}`)}</option>)}
            </select>
            <input type="number" value={form.price_low} onChange={(e) => setForm({ ...form, price_low: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.priceLow")} />
            <input type="number" value={form.price_high} onChange={(e) => setForm({ ...form, price_high: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.priceHigh")} />
            <input type="number" value={form.average_value} onChange={(e) => setForm({ ...form, average_value: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.averageValue")} />
          </div>

          {isDirectCupLike(form.method_group) && (
            <div className="mb-5 border border-blue-100 bg-blue-50 p-4">
              <h3 className="font-medium text-blue-950">{tr("groupA.title")}</h3>
              <p className="text-xs text-blue-800 mt-1 mb-4">
                {tr("groupA.description")}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <input
                  type="number"
                  value={form.direct_comparable_settings?.own_recent_weight_pct || ""}
                  onChange={(e) => setForm({ ...form, direct_comparable_settings: { ...(form.direct_comparable_settings || {}), own_recent_weight_pct: e.target.value } })}
                  className="border border-gray-200 px-3 py-2"
                  placeholder={tr("form.recentWeight")}
                />
                <input
                  type="number"
                  value={form.direct_comparable_settings?.own_3to5_weight_pct || ""}
                  onChange={(e) => setForm({ ...form, direct_comparable_settings: { ...(form.direct_comparable_settings || {}), own_3to5_weight_pct: e.target.value } })}
                  className="border border-gray-200 px-3 py-2"
                  placeholder={tr("form.oldWeight")}
                />
                <input
                  type="number"
                  value={form.direct_comparable_settings?.gallery_cap_value || ""}
                  onChange={(e) => setForm({ ...form, direct_comparable_settings: { ...(form.direct_comparable_settings || {}), gallery_cap_value: e.target.value } })}
                  className="border border-gray-200 px-3 py-2"
                  placeholder={tr("form.galleryCap")}
                />
                <input
                  type="number"
                  value={form.direct_comparable_settings?.min_references || ""}
                  onChange={(e) => setForm({ ...form, direct_comparable_settings: { ...(form.direct_comparable_settings || {}), min_references: e.target.value } })}
                  className="border border-gray-200 px-3 py-2"
                  placeholder={tr("form.minReferences")}
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <input
                  type="number"
                  value={form.direct_comparable_settings?.dimension_fit_pct || ""}
                  onChange={(e) => setForm({ ...form, direct_comparable_settings: { ...(form.direct_comparable_settings || {}), dimension_fit_pct: e.target.value } })}
                  className="border border-gray-200 px-3 py-2"
                  placeholder={tr("form.dimension")}
                />
                <input
                  type="number"
                  value={form.direct_comparable_settings?.technique_fit_pct || ""}
                  onChange={(e) => setForm({ ...form, direct_comparable_settings: { ...(form.direct_comparable_settings || {}), technique_fit_pct: e.target.value } })}
                  className="border border-gray-200 px-3 py-2"
                  placeholder={tr("form.technique")}
                />
                <input
                  type="number"
                  value={form.direct_comparable_settings?.proximity_fit_pct || ""}
                  onChange={(e) => setForm({ ...form, direct_comparable_settings: { ...(form.direct_comparable_settings || {}), proximity_fit_pct: e.target.value } })}
                  className="border border-gray-200 px-3 py-2"
                  placeholder={tr("form.proximity")}
                />
                <input
                  type="number"
                  value={form.direct_comparable_settings?.freshness_fit_pct || ""}
                  onChange={(e) => setForm({ ...form, direct_comparable_settings: { ...(form.direct_comparable_settings || {}), freshness_fit_pct: e.target.value } })}
                  className="border border-gray-200 px-3 py-2"
                  placeholder={tr("form.freshness")}
                />
              </div>
            </div>
          )}

          {isPointLike(form.method_group) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <input type="number" value={form.point_rate_low_eur} onChange={(e) => setForm({ ...form, point_rate_low_eur: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.pointLow")} />
              <input type="number" value={form.point_rate_high_eur} onChange={(e) => setForm({ ...form, point_rate_high_eur: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.pointHigh")} />
            </div>
          )}

          {isAnalogyLike(form.method_group) && (
            <div className="mb-5 border border-purple-100 bg-purple-50 p-4">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h3 className="font-medium text-purple-950">{tr("groupB.title")}</h3>
                  <p className="text-xs text-purple-800 mt-1">
                    {tr("groupB.description")}
                  </p>
                </div>
                <button type="button" onClick={addAnalogComparable} className="text-xs border border-purple-300 bg-white px-3 py-1 hover:border-purple-700">
                  + {tr("actions.add")}
                </button>
              </div>

              <div className="space-y-4">
                {(form.analog_comparables || []).map((row, index) => (
                  <div key={index} className="bg-white border border-purple-100 p-4">
                    <div className="flex justify-between gap-3 mb-3">
                      <strong className="text-sm">{tr("groupB.comparable", { number: index + 1 })}</strong>
                      <button type="button" onClick={() => removeAnalogComparable(index)} className="text-xs text-red-600">{tr("actions.delete")}</button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <input value={row.artist || ""} onChange={(e) => updateAnalogComparable(index, { artist: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.comparableArtist")} />
                      <input value={row.reason || ""} onChange={(e) => updateAnalogComparable(index, { reason: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.logic")} />
                      <input value={row.source || ""} onChange={(e) => updateAnalogComparable(index, { source: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.source")} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                      <select value={row.market_role || "trend_only"} onChange={(e) => updateAnalogComparable(index, { market_role: e.target.value })} className="border border-gray-200 px-3 py-2">
                        <option value="trend_only">{tr("marketRoles.trendOnly")}</option>
                        <option value="context">{tr("marketRoles.context")}</option>
                        <option value="price_comparable">{tr("marketRoles.priceComparable")}</option>
                      </select>
                      <select value={row.market_tier || "equivalent"} onChange={(e) => updateAnalogComparable(index, { market_tier: e.target.value })} className="border border-gray-200 px-3 py-2">
                        <option value="inferior">{tr("marketTiers.inferior")}</option>
                        <option value="equivalent">{tr("marketTiers.equivalent")}</option>
                        <option value="superior">{tr("marketTiers.superior")}</option>
                        <option value="different">{tr("marketTiers.different")}</option>
                      </select>
                      <input type="number" value={row.career_discount_pct || ""} onChange={(e) => updateAnalogComparable(index, { career_discount_pct: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.careerDiscount")} />
                      <input type="number" value={row.price_weight_max_pct || ""} onChange={(e) => updateAnalogComparable(index, { price_weight_max_pct: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.maxPriceWeight")} />
                    </div>

                    <label className="flex items-center gap-2 text-xs text-purple-800 mb-3">
                      <input type="checkbox" checked={Boolean(row.expert_override)} onChange={(e) => updateAnalogComparable(index, { expert_override: e.target.checked })} />
                      {tr("groupB.expertOverride")}
                    </label>

                    <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
                      <input type="number" value={row.observed_low || ""} onChange={(e) => updateAnalogComparable(index, { observed_low: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.observedLow")} />
                      <input type="number" value={row.observed_high || ""} onChange={(e) => updateAnalogComparable(index, { observed_high: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.observedHigh")} />
                      <input type="number" value={row.discount_pct || ""} onChange={(e) => updateAnalogComparable(index, { discount_pct: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.discount")} />
                      <input type="number" value={row.weight_pct || ""} onChange={(e) => updateAnalogComparable(index, { weight_pct: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.weight")} />
                      <input type="number" value={row.dimension_fit_pct || ""} onChange={(e) => updateAnalogComparable(index, { dimension_fit_pct: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.dimension")} />
                      <input type="number" value={row.technique_fit_pct || ""} onChange={(e) => updateAnalogComparable(index, { technique_fit_pct: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.technique")} />
                      <input type="number" value={row.proximity_fit_pct || ""} onChange={(e) => updateAnalogComparable(index, { proximity_fit_pct: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.proximity")} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <input type="number" value={row.freshness_fit_pct || ""} onChange={(e) => updateAnalogComparable(index, { freshness_fit_pct: e.target.value })} className="border border-gray-200 px-3 py-2" placeholder={tr("form.freshness")} />
                      <p className="text-xs text-gray-500 self-center">
                        {tr("groupB.adjustedResult")} {(() => {
                          const preview = computeGroupBAnalogRange({ ...form, analog_comparables: [row] });
                          return preview ? `${formatMoney(preview.low)} – ${formatMoney(preview.high)}` : "—";
                        })()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {groupBAnalogRange && (
                <div className="mt-4 bg-white border border-purple-200 p-3 text-sm text-purple-950">
                  {tr("groupB.calculatedRange")} <strong>{formatMoney(groupBAnalogRange.low)} – {formatMoney(groupBAnalogRange.high)}</strong>, {tr("groupB.average")} {formatMoney(groupBAnalogRange.average)}.
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <select value={form.confidence} onChange={(e) => setForm({ ...form, confidence: e.target.value })} className="border border-gray-200 px-3 py-2">
              {Object.entries(CONFIDENCE).map(([key, label]) => <option key={key} value={key}>{tr("confidence.label")} {tr(`confidence.${label}`)}</option>)}
            </select>
            <input type="date" value={form.quote_date} onChange={(e) => setForm({ ...form, quote_date: e.target.value })} className="border border-gray-200 px-3 py-2" />
          </div>

          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full border border-gray-200 px-3 py-2 mb-5 text-sm" placeholder={tr("form.notes")} />

          <div className="flex gap-3">
            <button type="button" onClick={saveQuote} disabled={saving} className="bg-black text-white px-6 py-3 text-sm disabled:opacity-40">
              {saving ? tr("actions.saving") : editingId ? tr("actions.update") : tr("actions.save")}
            </button>
            {editingId && <button type="button" onClick={resetForm} className="border border-gray-300 px-6 py-3 text-sm">{tr("actions.cancel")}</button>}
          </div>
        </section>

        <section className="bg-white border border-gray-200 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
            <h2 className="text-2xl font-serif">{tr("list.title")}</h2>
            <input value={search} onChange={(e) => setSearch(e.target.value)} className="border border-gray-200 px-3 py-2 text-sm" placeholder={tr("list.search")} />
          </div>

          <div className="border border-gray-200 divide-y divide-gray-100">
            {filteredQuotes.length === 0 && <div className="p-6 text-sm text-gray-500">{tr("list.empty")}</div>}

            {filteredQuotes.map((quote) => (
              <div key={quote.id} className="p-5 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                  <p className="font-serif text-lg">{quote.artist}</p>
                  <p className="text-sm text-gray-500">{quote.medium} · {METHOD_GROUPS[quote.method_group] ? tr(`methods.${METHOD_GROUPS[quote.method_group]}`) : quote.method_group}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {quote.quote_date || String(quote.updated_at || "").slice(0, 10)} · {tr("confidence.label")} {CONFIDENCE[quote.confidence] ? tr(`confidence.${CONFIDENCE[quote.confidence]}`) : quote.confidence}
                  </p>
                  {isDirectCupLike(quote.method_group) && quote.direct_comparable_settings && (
                    <p className="text-xs text-blue-700 mt-2">
                      {tr("list.groupARecorded")}
                    </p>
                  )}
                  {isAnalogyLike(quote.method_group) && Array.isArray(quote.analog_artists) && quote.analog_artists.length > 0 && (
                    <p className="text-xs text-purple-700 mt-2">
                      {tr("list.groupB")} {quote.analog_artists.join(" / ")}
                    </p>
                  )}
                  {isAnalogyLike(quote.method_group) && quote.analogy_reason && (
                    <p className="text-xs text-purple-500 mt-1 max-w-2xl">{quote.analogy_reason}</p>
                  )}
                  {isAnalogyLike(quote.method_group) && quote.analog_market_rules && Object.keys(quote.analog_market_rules || {}).length > 0 && (
                    <p className="text-xs text-purple-600 mt-1">
                      {tr("list.marketRules", { count: Object.keys(quote.analog_market_rules || {}).length })}
                    </p>
                  )}
                  {quote.notes && <p className="text-sm text-gray-600 mt-2 max-w-2xl whitespace-pre-wrap">{quote.notes}</p>}
                </div>

                <div className="text-right">
                  <p className="font-semibold">{quote.price_low ? formatMoney(quote.price_low) : "—"} – {quote.price_high ? formatMoney(quote.price_high) : "—"}</p>
                  <p className="text-xs text-gray-400">{tr("list.average")} {formatMoney(quote.average_value || 0)}</p>
                  <div className="flex justify-end gap-2 mt-3">
                    <button type="button" onClick={() => editQuote(quote)} className="text-xs border border-gray-300 px-3 py-1 hover:border-black">{tr("actions.edit")}</button>
                    <button type="button" onClick={() => deleteQuote(quote.id)} className="text-xs border border-red-200 text-red-600 px-3 py-1 hover:border-red-500">{tr("actions.delete")}</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
